import {
    constructTable,
    createCoreRowModel,
    rowPaginationFeature,
    tableFeatures,
} from '@tanstack/table-core';
import { storeReactivityBindings } from '@tanstack/table-core/store-reactivity-bindings';
import { FlexRender } from '@tanstack/table-core/flex-render';

/**
 * Tabela rankingu na TanStack Table (rdzeń bez adaptera frameworkowego).
 *
 * Stronicowanie jest po stronie serwera (`manualPagination`), więc tabela nigdy nie trzyma
 * więcej niż jednej strony wyników: `data` to dokładnie to, co przyszło z API, a stan
 * paginacji (`pageIndex`, `pageSize`) jest jedynym stanem, jaki tabela posiada. Rozmiar
 * całości podajemy przez `rowCount` — z niego liczy się `getPageCount()` i podsumowanie
 * pod tabelą.
 *
 * Sortowania celowo nie ma: ranking jest z definicji uporządkowany po miejscu, a API
 * innego porządku nie oddaje.
 */

const EM_DASH = '—';

const numberFormat = new Intl.NumberFormat('pl-PL');

/** Kolumny publicznej klasyfikacji — dokładnie to, co oddaje RankingRow::toArray(). */
const columns = [
    {
        accessorKey: 'position',
        header: 'Miejsce',
        cell: ({ getValue }) => numberFormat.format(getValue()),
    },
    {
        accessorKey: 'phone',
        header: 'Nr telefonu',
        // Numery krótsze niż siedem cyfr backend zasłania w całości, a brak numeru oddaje
        // nullem — w obu wypadkach nie ma czego pokazać poza kreską.
        cell: ({ getValue }) => getValue() ?? EM_DASH,
    },
    {
        accessorKey: 'score',
        header: 'Wynik',
        cell: ({ getValue }) => numberFormat.format(getValue()),
    },
    {
        id: 'prize',
        accessorFn: (row) => row.prize?.name ?? null,
        header: 'Nagroda',
        cell: ({ getValue }) => getValue() ?? EM_DASH,
    },
];

const features = tableFeatures({
    coreReactivityFeature: storeReactivityBindings(),
    coreRowModel: createCoreRowModel(),
    rowPaginationFeature,
});

/**
 * @param {{ pageIndex: number, pageSize: number }} pagination - stan startowy
 */
export function createRankingTable(pagination) {
    return constructTable({
        features,
        columns,
        data: [],
        // Cała klasyfikacja siedzi w bazie; przeglądarka dostaje jedną stronę i tyle.
        manualPagination: true,
        rowCount: 0,
        initialState: { pagination },
    });
}

/**
 * Stan paginacji. W v9 stan tabeli żyje w atomach, a `table.store` jest ich płaskim,
 * tylko-do-czytania widokiem — nie ma już `table.getState()` z v8.
 *
 * @returns {{pageIndex: number, pageSize: number}}
 */
export function paginationState(table) {
    return table.store.state.pagination;
}

/** Podmiana zawartości strony. Nowa tablica jest konieczna — memo tabeli patrzy na referencję. */
export function setTableData(table, rows, rowCount) {
    table.setOptions({ data: [...rows], rowCount });
}

export function renderHead(table, thead) {
    thead.replaceChildren(
        ...table.getHeaderGroups().map((headerGroup) => {
            const tr = document.createElement('tr');

            headerGroup.headers.forEach((header) => {
                const th = document.createElement('th');
                th.scope = 'col';
                th.dataset.rankingColumn = header.column.id;
                th.textContent = String(FlexRender({ header }) ?? '');
                tr.append(th);
            });

            return tr;
        }),
    );
}

export function renderBody(table, tbody) {
    tbody.replaceChildren(
        ...table.getRowModel().rows.map((row) => {
            const tr = document.createElement('tr');

            row.getAllCells().forEach((cell) => {
                const td = document.createElement('td');
                td.dataset.rankingColumn = cell.column.id;
                td.textContent = String(FlexRender({ cell }) ?? '');
                tr.append(td);
            });

            return tr;
        }),
    );
}
